
require("./../node_modules/@module-federation/nextjs-mf/src/include-defaults.js");
import dynamic from 'next/dynamic';
import { GetServerSidePropsContext } from 'next';

import DocumentLayout from 'global/DocumentLayout';
import If from 'global/Common/If';
import MainLayout from 'global/MainLayout';
import SEO from 'global/SEO';
import authCookie from 'utils/authCookie';
import useProtectedRoute from 'hooks/useProtectedRoute';
import userApi from 'lib/api/user';
import { ProfileGraphInfo } from 'features/user/Guest/types';
import { generateMetatags, isUsername } from 'features/user/Guest/utils';

const GuestUserPage = dynamic(() => import('features/user/Guest'));
const PageLoader = dynamic(() => import('global/Loading/PageLoader'), {
  ssr: false,
});
const User = dynamic(() => import('features/user'), { ssr: false });

interface UserPageProps {
  username: string;
  user: ProfileGraphInfo | null;
  isLoggedIn?: boolean;
}

function UserPage({ username, user, isLoggedIn }: UserPageProps) {
  return (
    <>
      <SEO {...generateMetatags(user)} />

      <If condition={!isLoggedIn}>
        <DocumentLayout noBox>
          <GuestUserPage username={username} user={user} />
        </DocumentLayout>
      </If>

      <If condition={isLoggedIn}>
        <MainLayout>
          <PageLoader loaded={isLoggedIn && useProtectedRoute()?.isLoaded} />
          <User username={username} />
        </MainLayout>
      </If>
    </>
  );
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  try {
    const isLoggedIn = Boolean(authCookie.getIsLoggedIn(context));
    const { params } = context;
    const username = params?.username as string;

    // Not valid username
    if (!isUsername(username)) {
      throw Error('Username is not valid');
    }

    // Get data profile through CSR if user already login
    if (isLoggedIn) {
      return {
        props: {
          username,
          user: null,
          isLoggedIn,
        },
      };
    }

    // Get data profile through SSR if user not login
    const response = await userApi.getGuestUserProfileGraph(username);

    // Valid username with no data
    if (!response.data.data?.profile) {
      throw Error('Username is not found');
    }

    return {
      props: {
        username,
        user: response.data.data,
        isLoggedIn,
      },
    };
  } catch (error) {
    return {
      notFound: true,
    };
  }
}

export default UserPage;
